// ============================================================================
// Incentive Program module — month-end No-Miss + quarterly Compliment.
//
// Tabs:
//   Queue      — periods needing action (generate / validate / route)
//   Payouts    — per-row validate / override for a selected period
//   Analytics  — payout rate per sub, cost per period, compliment rate
//   Finance    — the approved list Jose works from (+ CSV export)
//
// Money is RLS-scoped at the DB (finance + full-AM see all; an AM sees only
// their facilities; everyone else is excluded). Action buttons are gated by
// role on top of that: full-AM generates + routes + marks paid; an AM
// validates / overrides their own rows; finance is read-only.
// ============================================================================

import { hasFullAmAuthority } from '../../shared/role-gates';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import type { IncentivePayoutType } from '../../shared/types';
import { QueueView } from './queue-view';
import { PayoutsView } from './payouts-view';
import { AnalyticsView } from './analytics-view';
import { FinanceHandoffView } from './finance-handoff-view';

export { IncentiveQueueWidget } from './incentive-queue-widget';

type TabId = 'queue' | 'payouts' | 'analytics' | 'finance';

// CW-N 1c. The tab lives in the URL so that every entry point — the nav leaf,
// the dashboard widget, the ⌘K palette, a bookmark — resolves to a known
// screen instead of whatever useState happened to default to. An absent or
// unrecognised ?tab= lands on the Month-end queue, which is the screen Jack
// works from.
const TAB_IDS: TabId[] = ['queue', 'payouts', 'analytics', 'finance'];

function readTab(raw: string | null): TabId {
  return TAB_IDS.includes(raw as TabId) ? (raw as TabId) : 'queue';
}

export interface PeriodSelection {
  period: string;
  payoutType: IncentivePayoutType;
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'queue', label: 'Month-end queue' },
  { id: 'payouts', label: 'Payouts' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'finance', label: 'Finance handoff' },
];

export default function IncentiveProgramPage() {
  const { profile } = useAuth();
  const role = profile?.role ?? null;
  const isFullAm = hasFullAmAuthority(role);

  // CW-N 3d — cross-view freshness. A status change made in Payouts must show
  // up in the Month-end queue without a manual refresh. The page owns one
  // counter; mutating screens bump it, reading hooks take it as a dep. No
  // state library: this module does not have one and does not need one.
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((n) => n + 1), []);

  const [params, setParams] = useSearchParams();
  const tab = readTab(params.get('tab'));
  const [selection, setSelection] = useState<PeriodSelection | null>(null);

  const setTab = useCallback(
    (next: TabId) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === 'queue') p.delete('tab');
          else p.set('tab', next);
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  function openPeriod(sel: PeriodSelection) {
    setSelection(sel);
    setTab('payouts');
  }

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6">
      <PageHeader
        eyebrow="Subcontractor performance payments · Scope Performance + Quarterly Compliment"
        title="Performance Payments"
        meta={
          <span className="text-xs text-tcs-text-muted">
            Pay window: Scope Performance for month M pays the 15th of M+1
          </span>
        }
      />

      <div className="mt-4 flex flex-wrap gap-1 border-b border-tcs-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={[
              'rounded-t-md px-3 py-2 text-sm transition-colors',
              tab === t.id
                ? 'border-b-2 border-tcs-text font-medium text-tcs-text'
                : 'text-tcs-text-secondary hover:text-tcs-text',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === 'queue' && (
          <QueueView
            isFullAm={isFullAm}
            onOpenPeriod={openPeriod}
            refreshKey={refreshKey}
          />
        )}
        {tab === 'payouts' && (
          <PayoutsView
            selection={selection}
            role={role}
            refreshKey={refreshKey}
            onMutated={bump}
            onClearSelection={() => setSelection(null)}
          />
        )}
        {tab === 'analytics' && <AnalyticsView />}
        {tab === 'finance' && <FinanceHandoffView />}
      </div>
    </div>
  );
}
